import { Popover as ArkPopover, usePopover } from '@ark-ui/react/popover'

import { useEnhancedChildren } from '~/hooks/use-enhanced-children'

import { cn } from '~/utils/cn'

import type { Assign } from '@ark-ui/react'
import React from 'react'
import { Button } from '../button'
import { root as rootStyles } from './popover.styles'

export type PlacementType = NonNullable<
  PopoverRootProps['positioning']
>['placement']

export type PopoverSharedProps = {
  placement?: PlacementType
  trigger?: React.ReactNode
  hasButtonClose?: boolean
  handleClickButtonClose?: () => void
}

export type PopoverRootProps = Assign<
  React.ComponentProps<typeof ArkPopover.Root>,
  PopoverSharedProps
>

export function PopoverRoot({
  children,
  placement = 'bottom-start',
  trigger,
  hasButtonClose,
  handleClickButtonClose,
  open,
  ...props
}: PopoverRootProps) {
  const popover = usePopover({
    open: open,
    positioning: {
      placement: placement,
    },
  })

  const hasPopoverButton = React.Children.toArray(children).some(child => {
    return (
      React.isValidElement(child) &&
      // biome-ignore lint/suspicious/noExplicitAny:
      (child.type as any)?.displayName === 'PopoverButton'
    )
  })

  const enhancedChildrenWithInjectProps = useEnhancedChildren(children, {
    targets: ['PopoverDescription', 'PopoverTitle', 'PopoverButton'],
    props: {
      hasPopoverButton,
    },
  })

  let popoverTitle: React.ReactNode = null
  let popoverTag: React.ReactNode = null

  const restChildren = React.Children.toArray(
    enhancedChildrenWithInjectProps
  ).filter(child => {
    if (!React.isValidElement(child)) return true
    // biome-ignore lint/suspicious/noExplicitAny:
    const displayName = (child.type as any)?.displayName

    if (displayName === 'PopoverTitle') {
      popoverTitle = child
      return false
    }

    if (displayName === 'PopoverTag') {
      popoverTag = child
      return false
    }

    return true
  })

  const arrowStyle: React.CSSProperties & Record<string, string> = {
    '--arrow-size': '8px',
    '--arrow-background': 'rgba(255, 255, 255, 1)',
  }

  return (
    <ArkPopover.RootProvider {...props} value={popover}>
      {trigger}
      <ArkPopover.Positioner>
        <ArkPopover.Content className={cn(rootStyles())}>
          <ArkPopover.Arrow style={arrowStyle}>
            <ArkPopover.ArrowTip
              style={{
                ...arrowStyle,
                ...(placement === 'top-start' || placement === 'bottom-start'
                  ? { left: 'calc(var(--spacing-stack-twelvepulse) * -1)' }
                  : {}),
              }}
            />
          </ArkPopover.Arrow>
          <div className="flex w-full flex-row items-center">
            {popoverTitle}
            {popoverTag}
            {hasButtonClose && (
              <ArkPopover.CloseTrigger
                className="ml-auto"
                data-testid="popover-close-trigger"
              >
                <Button.Root
                  data-testid="popover-button-close-trigger"
                  onClick={() => {
                    if (typeof handleClickButtonClose === 'function') {
                      handleClickButtonClose()
                    }
                  }}
                  size="ml"
                  variant="neutral-tertiary"
                >
                  <Button.Icon symbol="rdicon-dismiss" />
                </Button.Root>
              </ArkPopover.CloseTrigger>
            )}
          </div>
          {restChildren}
        </ArkPopover.Content>
      </ArkPopover.Positioner>
    </ArkPopover.RootProvider>
  )
}

PopoverRoot.displayName = 'PopoverRoot'
